import {
  Directive,
  Input,
  TemplateRef,
  ElementRef,
  OnInit,
  HostListener,
  ComponentRef,
  OnDestroy,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { CustomTooltipComponent } from './custom-tooltip.component';
import { ComponentPortal } from '@angular/cdk/portal';
import { takeUntil } from 'rxjs/operators';
import { DeviceInfoService } from '@core/services/device-info.service';
import { AllDeviceInfo } from '@interfaces';
import { ReplaySubject, fromEvent } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

@Directive({
  // eslint-disable-next-line
    selector: '[customTooltip]',
  standalone: false,
})
export class CustomTooltipDirective implements OnInit, OnDestroy {
  private readonly _overlay = inject(Overlay);
  private readonly _overlayPositionBuilder = inject(OverlayPositionBuilder);
  private readonly _elementRef = inject(ElementRef);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly platformId = inject(PLATFORM_ID);

  @Input() showToolTip = true;
  @Input('customTooltip') text: string;
  @Input() contentTemplate: TemplateRef<any>;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);
  private _overlayRef: OverlayRef | null = null;
  public deviceInfo: AllDeviceInfo;
  private isTooltipVisible: boolean = false;

  public ngOnInit(): void {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(info => {
      const previousDeviceType = this.deviceInfo?.deviceTypeDetected;
      const reopenTooltip = this.isTooltipVisible;

      this.deviceInfo = info;

      if (!this.showToolTip || !isPlatformBrowser(this.platformId) || previousDeviceType === info.deviceTypeDetected) {
        return;
      }

      this.disposeOverlay();
      this.createOverlay();

      if (reopenTooltip) {
        this.showTooltip();
      }
    });

    if (!this.showToolTip || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.createOverlay();

    fromEvent<MouseEvent>(document, 'click')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: MouseEvent) => {
        const target = event.target as Node;

        if (
          !this.isTooltipVisible ||
          !this._overlayRef ||
          this._elementRef.nativeElement.contains(target) ||
          this._overlayRef.overlayElement.contains(target)
        ) {
          return;
        }

        this.closeToolTip();
      });
  }

  @HostListener('mouseenter')
  public show(): void {
    if (this.showToolTip && !this.isTooltipVisible && this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
      this.showTooltip();
    }
  }

  @HostListener('mouseleave')
  public hide(): void {
    if (this.showToolTip && this.isTooltipVisible && this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
      this.closeToolTip();
    }
  }

  @HostListener('focusin')
  public showOnFocus(): void {
    if (this.showToolTip && this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
      this.showTooltip();
    }
  }

  @HostListener('focusout', ['$event'])
  public hideOnBlur(event: FocusEvent): void {
    if (!this.showToolTip || this.deviceInfo.deviceTypeDetected !== 'DESKTOP') {
      return;
    }

    const nextTarget = event.relatedTarget as Node | null;

    if (nextTarget && this._overlayRef?.overlayElement.contains(nextTarget)) {
      return;
    }

    this.closeToolTip();
  }

  @HostListener('keydown.escape')
  public closeOnEscape(): void {
    this.closeToolTip();
  }

  @HostListener('click', ['$event'])
  public toggleTooltip(event: MouseEvent): void {
    if (!this.showToolTip) {
      return;
    }

    if (this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (this.isTooltipVisible) {
      this.closeToolTip();
    } else {
      this.showTooltip();
    }
  }

  public ngOnDestroy(): void {
    this.closeToolTip();
    this.disposeOverlay();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private closeToolTip(): void {
    if (this._overlayRef?.hasAttached()) {
      this._overlayRef.detach();
    }

    this.isTooltipVisible = false;
  }

  private showTooltip(): void {
    if (!this._overlayRef) {
      this.createOverlay();
    }

    if (this._overlayRef && !this._overlayRef.hasAttached()) {
      const tooltipRef: ComponentRef<CustomTooltipComponent> = this._overlayRef.attach(
        new ComponentPortal(CustomTooltipComponent)
      );
      tooltipRef.instance.text = this.text;
      tooltipRef.instance.contentTemplate = this.contentTemplate;
      tooltipRef.changeDetectorRef.detectChanges();
      this.isTooltipVisible = true;
    }
  }

  private createOverlay(): void {
    const isPhone = this.deviceInfo.deviceTypeDetected === 'PHONE';
    const positionStrategy = this._overlayPositionBuilder
      .flexibleConnectedTo(this._elementRef)
      .withViewportMargin(16)
      .withPush(true)
      .withFlexibleDimensions(false)
      .withPositions(
        isPhone
          ? [
              {
                originX: 'center',
                originY: 'bottom',
                overlayX: 'center',
                overlayY: 'top',
                offsetY: 12,
              },
              {
                originX: 'center',
                originY: 'top',
                overlayX: 'center',
                overlayY: 'bottom',
                offsetY: -12,
              },
            ]
          : [
              {
                originX: 'end',
                originY: 'center',
                overlayX: 'start',
                overlayY: 'center',
                offsetX: 12,
              },
              {
                originX: 'start',
                originY: 'center',
                overlayX: 'end',
                overlayY: 'center',
                offsetX: -12,
              },
              {
                originX: 'center',
                originY: 'top',
                overlayX: 'center',
                overlayY: 'bottom',
                offsetY: -12,
              },
              {
                originX: 'center',
                originY: 'bottom',
                overlayX: 'center',
                overlayY: 'top',
                offsetY: 12,
              },
            ]
      );

    this._overlayRef = this._overlay.create({
      positionStrategy,
      panelClass: isPhone
        ? ['custom-overlay-tooltip-pane', 'custom-overlay-tooltip-pane--phone']
        : 'custom-overlay-tooltip-pane',
      width: isPhone ? 'min(320px, calc(100vw - 32px))' : undefined,
      maxWidth: isPhone ? 'calc(100vw - 32px)' : '360px',
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
      hasBackdrop: isPhone,
      backdropClass: isPhone ? 'cdk-overlay-transparent-backdrop' : undefined,
    });

    if (isPhone) {
      this._overlayRef
        .backdropClick()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.closeToolTip();
        });
    }
  }

  private disposeOverlay(): void {
    this._overlayRef?.dispose();
    this._overlayRef = null;
    this.isTooltipVisible = false;
  }
}
